import type {
  Estate,
  PhysicalDrive,
  StorageFilesystem,
  StorageMachine,
  StorageSection,
  StorageShare,
  StorageVolume,
  VolumeWhere,
} from '@/db/api'
import { ESTATE_NAMES, roleWords, type Level } from './hosts'
import { fillOf, fmtBytes, pctOf, shareOf } from './storage'

/**
 * The infra pack's Storage page (components/StorageInventory.tsx,
 * /infra/storage), pure: when a drive, filesystem or volume needs a look,
 * the words for each row, and how the rows are grouped. internal/storage's
 * inventory reports facts only; the lines are here. Filesystems and cloud
 * volumes fill as Storage's disks do (85% a warning, 95% critical).
 */

/** Temperatures in °C past which a drive is warm (a warning) or hot (critical), by kind. */
export const TEMP_LINES: Record<string, { warn: number; critical: number }> = {
  hdd: { warn: 50, critical: 60 },
  ssd: { warn: 65, critical: 75 },
  sd: { warn: 65, critical: 75 },
  nvme: { warn: 70, critical: 80 },
}
/** a drive whose kind nothing says: between a spinning disk's lines and an SSD's */
export const TEMP_DEFAULT = { warn: 60, critical: 70 }

/** The share of a flash drive's rated endurance used past which it is a warning, and critical. */
export const WEAR_WARN = 0.8
export const WEAR_CRITICAL = 1

const worst = (levels: Level[]): Level =>
  levels.includes('critical') ? 'critical' : levels.includes('warn') ? 'warn' : 'ok'

export const tempLevel = (d: PhysicalDrive): Level => {
  if (d.temperature === undefined) return 'ok'
  const line = (d.kind && TEMP_LINES[d.kind]) || TEMP_DEFAULT
  return d.temperature >= line.critical ? 'critical' : d.temperature >= line.warn ? 'warn' : 'ok'
}

export const wearLevel = (d: PhysicalDrive): Level =>
  d.wear === undefined ? 'ok' : d.wear >= WEAR_CRITICAL ? 'critical' : d.wear >= WEAR_WARN ? 'warn' : 'ok'

/** A drive's level: failing SMART is critical, then its temperature and wear. Asleep is not judged. */
export const driveLevel = (d: PhysicalDrive): Level =>
  worst([d.smart === 'failing' ? 'critical' : 'ok', tempLevel(d), wearLevel(d)])

const KINDS: Record<string, string> = {
  nvme: 'NVMe',
  ssd: 'SSD',
  hdd: 'Hard disk',
  sd: 'SD card',
  virtual: 'Virtual disk',
  volume: 'Cloud volume',
}

export const kindWords = (kind?: string): string => (kind ? (KINDS[kind] ?? '') : '')

/** Power-on time in one unit: 30 h, 12 days, 1.6 years. */
export const fmtHours = (h: number): string => {
  if (h < 48) return `${Math.max(0, Math.round(h))} h`
  const days = h / 24
  if (days < 365) return `${Math.floor(days)} days`
  const years = days / 365.25
  const n = years < 10 ? years.toFixed(1).replace(/\.0$/, '') : String(Math.round(years))
  return n === '1' ? '1 year' : `${n} years`
}

// a VM's disk and a cloud volume carry a model that only says so ("QEMU HARDDISK", "Volume")
const generic = (d: PhysicalDrive) => d.kind === 'virtual' || d.kind === 'volume'

/** A drive's name beside its device: its model, or its kind where the model says nothing more. */
export const driveTitle = (d: PhysicalDrive): string => (generic(d) ? kindWords(d.kind) : (d.model ?? ''))

/**
 * The facts under a drive's name, as one sentence of phrases: its kind, its
 * SMART check, temperature, power-on time and wear, or the claim a cloud volume holds.
 */
export const driveFacts = (d: PhysicalDrive): string[] => {
  const out: string[] = []
  const kind = kindWords(d.kind)
  if (kind && !generic(d)) out.push(kind)
  if (d.volume) out.push(`holds ${d.volume}`)
  if (d.smart === 'ok') out.push('SMART passes')
  if (d.smart === 'failing') out.push('SMART fails')
  if (d.smart === 'asleep') out.push('asleep, so SMART not read')
  if (d.temperature !== undefined) out.push(`${Math.round(d.temperature)} °C`)
  if (d.power_on_hours !== undefined) out.push(`on for ${fmtHours(d.power_on_hours)}`)
  if (d.wear !== undefined) out.push(`${Math.round(d.wear * 100)}% worn`)
  return out
}

/** A filesystem's level by how full it is (Storage's lines). */
export const fsLevel = (f: Pick<StorageFilesystem, 'size' | 'used'>): Level => fillOf(shareOf(f))

/** A filesystem's type as the page words it: a mergerfs pool says so. */
export const fsTypeWords = (type: string): string => (type === 'mergerfs' ? 'mergerfs pool' : type)

/** "483 GB of 1 TB, 48%" */
export const fsWords = (f: Pick<StorageFilesystem, 'size' | 'used'>): string =>
  `${fmtBytes(f.used)} of ${fmtBytes(f.size)}`

const ESTATE_ORDER: Estate[] = ['home', 'hetzner', 'oracle', 'other']

/** Machines by estate (home first, as the server orders them), an estate with none left out. */
export const machinesByEstate = <M extends StorageMachine>(
  hosts: M[],
): { estate: Estate; name: string; hosts: M[] }[] =>
  ESTATE_ORDER.map((estate) => ({
    estate,
    name: ESTATE_NAMES[estate],
    hosts: hosts.filter((h) => h.estate === estate),
  })).filter((g) => g.hosts.length > 0)

/** A machine's line under its name: its role and where it runs. */
export const machineLine = (m: StorageMachine): string =>
  [m.role === 'node' ? 'Cluster node' : roleWords(m.role), m.note].filter(Boolean).join(', ')

export const WHERE_NAMES: Record<VolumeWhere, string> = {
  cloud: 'Hetzner volumes',
  box: 'On the Storage Box',
  nfs: 'On the NAS over NFS',
  local: "On a node's own disk",
  other: 'Elsewhere',
}

const WHERE_ORDER: VolumeWhere[] = ['cloud', 'box', 'nfs', 'local', 'other']

/** The volumes by where they live, each group in the server's order (namespace, claim); empty groups left out. */
export const volumesByWhere = (
  vols: StorageVolume[],
): { where: VolumeWhere; name: string; volumes: StorageVolume[] }[] =>
  WHERE_ORDER.map((where) => ({
    where,
    name: WHERE_NAMES[where],
    volumes: vols.filter((v) => v.where === where),
  })).filter((g) => g.volumes.length > 0)

/**
 * A volume's level: a claim not bound (Pending, Lost) or a volume no claim
 * holds is a warning; a cloud volume fills as a disk does.
 */
export const volumeLevel = (v: StorageVolume): Level => {
  const bound = v.claim ? v.phase === 'Bound' : false
  const fill =
    v.used !== undefined && v.capacity > 0 ? fillOf(shareOf({ size: v.capacity, used: v.used })) : 'ok'
  return worst([bound ? 'ok' : 'warn', fill])
}

/** What a volume uses: "2.3 GB of 21 GB", or its capacity alone where its use is the whole disk's. */
export const volumeSize = (v: StorageVolume): string =>
  v.used !== undefined && v.capacity > 0
    ? `${fmtBytes(v.used)} of ${fmtBytes(v.capacity)}`
    : fmtBytes(v.capacity)

/** Where a volume is, after its namespace: its class, its node. */
export const volumeLine = (v: StorageVolume): string =>
  [v.namespace, v.class, v.node ? `on ${v.node}` : '', v.claim ? '' : `no claim, ${v.phase.toLowerCase()}`]
    .filter(Boolean)
    .join(', ')

/** The percentage a cloud volume is full, for its bar; undefined without figures. */
export const volumePct = (v: StorageVolume): number | undefined =>
  v.used !== undefined && v.capacity > 0 ? pctOf(shareOf({ size: v.capacity, used: v.used })) : undefined

/** "Backed up by forgejo", or that nothing by its name was found. */
export const backupWords = (v: StorageVolume): string =>
  v.backup === 'covered' && v.backed_up_by?.length
    ? `Backed up by ${v.backed_up_by.join(' and ')}`
    : 'No backup found by its name'

/** The facts under a share's name (an export named by its path says the path once). */
export const shareFacts = (s: StorageShare): string[] => {
  const out: string[] = []
  if (s.path && s.path !== s.name) out.push(s.path)
  out.push(s.access === 'ro' ? 'read only' : 'read and write')
  if (s.guest) out.push('guests welcome')
  if (s.allowed?.length) out.push(`lets in ${s.allowed.join(', ')}`)
  return out
}

export const clientsWords = (n: number): string => (n === 1 ? '1 connected' : `${n} connected`)

/** NFS exports first, then SMB shares, each by name. */
export const sortShares = (shares: StorageShare[]): StorageShare[] =>
  [...shares].sort((a, b) =>
    a.protocol !== b.protocol ? (a.protocol === 'nfs' ? -1 : 1) : a.name.localeCompare(b.name),
  )

/** A section that is off, in a sentence; null when it is not off. */
export const offWords = (s: StorageSection): string | null => {
  if (s.state !== 'off') return null
  switch (s.off) {
    case 'rbac':
      return "The app may not read the cluster's volumes yet: its ClusterRole needs get and list on persistentvolumes, persistentvolumeclaims and storageclasses."
    case 'cluster':
      return 'This server is not running in a cluster, so there are no volumes to read.'
    default:
      return 'Not set up on this server: ALFIEOS_PROMETHEUS_URL is not set.'
  }
}

/** The worst level among a machine's drives, for its heading. */
export const drivesLevel = (drives: PhysicalDrive[]): Level => worst(drives.map(driveLevel))
